import React, { useEffect, useRef, useState } from 'react';
import { I } from './Icons';
import { useDecode, money, num, reduced } from './useHomeData';

const TZ = 'Australia/Hobart';

// ---------- Hero ----------
export const Hero = ({ firstName, queue, onStart, onGamePlan }) => {
  const h = +new Date().toLocaleString('en-AU', { timeZone: TZ, hour: 'numeric', hour12: false });
  const full = `${h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'}, ${firstName || 'there'}.`;
  const [typed, setTyped] = useState(reduced() ? full : '');
  useEffect(() => {
    if (reduced()) { setTyped(full); return undefined; }
    let i = 0; let t; const step = () => { i += 1; setTyped(full.slice(0, i)); if (i < full.length) t = setTimeout(step, 28 + Math.random() * 30); };
    t = setTimeout(step, 250); return () => clearTimeout(t);
  }, [full]);
  const today = new Date().toLocaleDateString('en-AU', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
  const items = queue.items || [];
  const overdue = items.filter((q) => q.type === 'follow' && q.why === 'Overdue').length;
  const follows = items.filter((q) => q.type === 'follow').length - overdue;
  const waiting = items.filter((q) => !['follow', 'task', 'jarvis'].includes(q.type)).length;
  const flagged = items.filter((q) => q.type === 'jarvis').length;
  const tasks = items.filter((q) => q.type === 'task').length;
  const parts = [];
  if (overdue) parts.push(<React.Fragment key="o"><strong>{overdue} follow-up{overdue > 1 ? 's' : ''}</strong> overdue</React.Fragment>);
  if (follows) parts.push(<React.Fragment key="f"><strong>{follows} follow-up{follows > 1 ? 's' : ''}</strong> due today</React.Fragment>);
  if (waiting) parts.push(<React.Fragment key="w"><strong>{waiting} client action{waiting > 1 ? 's' : ''}</strong> waiting on the team</React.Fragment>);
  if (flagged) parts.push(<React.Fragment key="j"><strong>{flagged} Jarvis recommendation{flagged > 1 ? 's' : ''}</strong> in your queue</React.Fragment>);
  if (tasks) parts.push(<React.Fragment key="t"><strong>{tasks} task{tasks > 1 ? 's' : ''}</strong> on your list</React.Fragment>);
  return (
    <div className="hello rv" style={{ animationDelay: '.05s' }}>
      <div className="eyebrow"><span className="ld blue" /><span data-testid="hero-date">{today}</span></div>
      <h1><span className="grad" data-testid="header-greeting">{typed}</span>{typed.length < full.length && <span className="caret" aria-hidden="true" />}</h1>
      <p className="lede" data-testid="hero-lede">
        {!items.length ? <><strong>Queue clear.</strong> Everything waiting on you is done.</> : <>{parts.map((p, i) => <React.Fragment key={i}>{i > 0 && '. '}{p}</React.Fragment>)}. Every next step is drafted and ready to go.</>}
      </p>
      <div className="hello-actions">
        <a className="btn btn-accent" href="#queue" onClick={(e) => { e.preventDefault(); onStart(); }} data-testid="hero-start-queue"><I id="arrow" size={16} />Start my queue</a>
        <a className="btn btn-ghost" href="/game-plan" onClick={(e) => { e.preventDefault(); onGamePlan(); }} data-testid="hero-open-gameplan"><I id="target" size={16} />Open Game Plan</a>
      </div>
    </div>
  );
};


// Jarvis · Chief of Staff — today's brief + what it's watching. Connection status stays as a small line for admins.
const Chip = ({ n, label, cls, testid, pulse }) => (
  <span className={`jvs-chip ${cls}`} data-testid={testid}><i className={pulse && n ? 'pulse' : ''} /><b>{n}</b>{label}</span>
);

export const JarvisCore = ({ j, cos, onOpen, onManage, onChat }) => {
  const live = j && (j.webhook_on || j.api_on);
  const c = cos?.counts || {};
  return (
    <aside className="core jvs rv" style={{ animationDelay: '.15s' }} aria-label="Jarvis Chief of Staff" data-testid="home-jarvis-card">
      <div className="jvs-top">
        <div className="orb jvs-orb" aria-hidden="true" data-testid="home-jarvis-orb"><span className="r" /><span className="r r2" /><span className="c" /></div>
        <div className="jvs-title"><b>Jarvis</b><span>Chief of Staff</span></div>
        {j?.is_admin && <button type="button" className="jvs-link" onClick={onManage} data-testid="home-jarvis-connect-agent-link">{live ? 'Agent connected' : 'Connect agent'}</button>}
      </div>
      <p className="jvs-brief" data-testid="home-jarvis-headline">{cos?.brief?.headline || 'Reading the whole business…'}</p>
      <div className="jvs-chips">
        <Chip n={c.urgent ?? 0} label="Urgent" cls="u" testid="home-jarvis-urgent-count" pulse />
        <Chip n={c.important ?? 0} label="Important" cls="i" testid="home-jarvis-important-count" />
        <Chip n={c.later ?? 0} label="Can wait" cls="w" testid="home-jarvis-wait-count" />
      </div>
      <div className="jvs-actions">
        <button type="button" className="btn btn-solid btn-sm" onClick={onOpen} data-testid="home-jarvis-open-button"><I id="spark" size={14} />Open</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onChat} data-testid="home-jarvis-chat-button"><I id="mic" size={14} />Chat</button>
      </div>
    </aside>
  );
};

// ---------- Ticker ----------
const DOT = { portal: 'var(--green)', esign: 'var(--violet)', idv: 'var(--sky)', lodged: 'var(--accent)', settled: 'var(--cyan)', approved: 'var(--accent)', lead: 'var(--amber)' };
const ago = (iso) => { const s = (Date.now() - new Date(iso).getTime()) / 1000; if (s < 60) return 'just now'; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; };
export const Ticker = ({ activity, week }) => {
  const items = [
    ...activity.map((a) => <span className="tk" key={a.id}><span className="dot" style={{ background: DOT[a.kind] || 'var(--accent)' }} /><b>{a.name}</b>{a.text}<span className="mono">{ago(a.at)}</span></span>),
    week ? <span className="tk" key="appr"><span className="dot" style={{ background: 'var(--accent)' }} /><b>{num(week.approvals)} approvals</b>this week</span> : null,
    week ? <span className="tk" key="leads"><span className="dot" style={{ background: 'var(--cyan)' }} /><b>{num(week.leads)} new leads</b>this week</span> : null,
  ].filter(Boolean);
  if (!items.length) items.push(<span className="tk" key="none"><span className="dot" style={{ background: 'var(--faint)' }} />Listening for workspace activity…</span>);
  return (
    <section className="ticker rv" style={{ animationDelay: '.25s' }} aria-label="Live activity" data-testid="live-ticker">
      <div className="ticker-label"><span className="ld" />Live</div>
      <div className="ticker-track"><div className="ticker-run">{items}{items.map((el) => React.cloneElement(el, { key: `${el.key}_2` }))}</div></div>
    </section>
  );
};

// ---------- Performance ----------
const KPI = ({ label, ic, value, sub, bar, flash, testid }) => {
  const d = useDecode(value);
  return (
    <div className={`kpi${flash ? ' flash' : ''}`} data-testid={testid}>
      <div className="kpi-top"><span className="kpi-label">{label}</span><span className="kpi-ic"><I id={ic} /></span></div>
      <div className="kpi-val">{d}</div>
      {bar != null && <div className="bar"><i style={{ width: `${bar}%` }} /></div>}
      <div className="kpi-sub">{sub}</div>
    </div>
  );
};

export const Performance = ({ period, setPeriod, data, syncedAt, queue, onShowMe, onAnalytics }) => {
  const segRef = useRef(null); const [pill, setPill] = useState({ left: 3, width: 0 }); const [flash, setFlash] = useState(false);
  const [secs, setSecs] = useState(0);
  useEffect(() => { const t = setInterval(() => setSecs(Math.round((Date.now() - syncedAt) / 1000)), 1000); return () => clearInterval(t); }, [syncedAt]);
  useEffect(() => {
    const move = () => { const b = segRef.current?.querySelector('button[aria-pressed="true"]'); if (b) setPill({ left: b.offsetLeft, width: b.offsetWidth }); };
    move(); const t = setTimeout(move, 400); window.addEventListener('resize', move); document.fonts?.ready.then(move);
    return () => { clearTimeout(t); window.removeEventListener('resize', move); };
  }, [period]);
  const sub = period === 'week' ? 'This week' : period === 'today' ? 'Today' : 'This month';
  const d = data;
  const contacted = d ? d.leads - d.leadsToContact : 0;
  const pct = d && d.leads ? Math.round((contacted / d.leads) * 100) : 0;
  const ready = (queue.items || []).filter((q) => q.type !== 'follow' && q.type !== 'task').length;
  let insight; let showBtn = false;
  if (!d) insight = 'Connecting to this period…';
  else if (d.lodgements === 0 && ready) { insight = <>0 lodged {period === 'week' ? 'this week' : 'so far'}. <b>{ready} client file{ready > 1 ? 's are' : ' is'}</b> completed and waiting on the team. Clear them to get lodgements moving.</>; showBtn = true; }
  else if (d.leadsToContact) insight = <><b>{d.leadsToContact} new leads</b> still to contact. Speed to lead wins the deal.</>;
  else insight = 'Pipeline is moving. Nothing flagged right now.';
  return (
    <section className="perf rv" style={{ animationDelay: '.32s' }} aria-labelledby="perfTitle" data-testid="performance-panel">
      <div className="grid-bg" /><div className="sweep" />
      <div className="perf-head">
        <div className="perf-title"><h2 id="perfTitle">Performance</h2><span className="live"><span className="ld" style={{ width: 6, height: 6 }} />LIVE</span><span className="synced" data-testid="perf-synced">{secs < 5 ? 'Synced just now' : `Synced ${secs}s ago`}</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div className="seg" role="group" aria-label="Period" ref={segRef} data-testid="perf-period">
            <span className="pill" style={{ left: pill.left, width: pill.width }} />
            {[['today', 'Today'], ['week', 'This week'], ['month', 'This month']].map(([k, l]) => (
              <button type="button" key={k} aria-pressed={period === k} onClick={() => { setPeriod(k); setFlash(true); setTimeout(() => setFlash(false), 900); }} data-testid={`perf-period-${k}`}>{l}</button>
            ))}
          </div>
          <a className="perf-link" href="/game-plan" onClick={(e) => { e.preventDefault(); onAnalytics(); }} data-testid="perf-full-analytics">Full analytics<I id="arrow" size={14} /></a>
        </div>
      </div>
      <div className="kpis">
        <KPI label="Portals done" ic="file" value={num(d?.portals)} sub={sub} flash={flash} testid="kpi-portals" />
        <KPI label="Lodgements" ic="send2" value={num(d?.lodgements)} sub={<><b>{money(d?.lodgementValue)}</b> value</>} flash={flash} testid="kpi-lodgements" />
        <KPI label="Approvals" ic="badge" value={num(d?.approvals)} sub={sub} flash={flash} testid="kpi-approvals" />
        <KPI label="Settlements" ic="key2" value={num(d?.settlements)} sub={<><b>{money(d?.settlementValue)}</b> value</>} flash={flash} testid="kpi-settlements" />
        <KPI label="New leads" ic="lead" value={num(d?.leads)} sub={<><b>{d ? d.leadsToContact : ''}</b> still to contact</>} bar={pct} flash={flash} testid="kpi-leads" />
      </div>
      <div className="insight" data-testid="perf-insight">
        <I id="spark" /><span>{insight}</span>
        {showBtn && <button type="button" onClick={onShowMe} data-testid="perf-show-me">Show me<I id="arrow" size={14} /></button>}
      </div>
    </section>
  );
};
